const DEFAULT_FALLBACK = 'unknown';
const ARGOS_BASE_PATH = '/argos';

const normalizeWhitespace = value => `${value ?? ''}`.trim().replace(/\s+/g, ' ');

export const normalizeAnalyticsValue = (value, fallback = DEFAULT_FALLBACK) => {
  const normalized = normalizeWhitespace(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  return normalized || fallback;
};

export const stripArgosBasePath = path => {
  if (!path) {
    return '/';
  }

  const normalizedPath = `${path}`.trim();

  if (!normalizedPath.startsWith(ARGOS_BASE_PATH)) {
    return normalizedPath || '/';
  }

  const strippedPath = normalizedPath.slice(ARGOS_BASE_PATH.length);

  return strippedPath.startsWith('/') ? strippedPath : `/${strippedPath}`;
};

export const sanitizeAnalyticsPath = path => {
  const strippedPath = stripArgosBasePath(path);
  const pathWithoutQuery = strippedPath.split('?')[0].split('#')[0];

  if (!pathWithoutQuery) {
    return '/';
  }

  return pathWithoutQuery.startsWith('/') ? pathWithoutQuery : `/${pathWithoutQuery}`;
};

export const sanitizeAnalyticsUrl = url => {
  if (!url) {
    return '/';
  }

  try {
    return sanitizeAnalyticsPath(new URL(url, window.location.origin).pathname);
  } catch (error) {
    return sanitizeAnalyticsPath(url);
  }
};

export const getAnalyticsQueryLengthBucket = query => {
  const length = normalizeWhitespace(query).length;

  if (length === 0) return 'empty';
  if (length <= 3) return '1_3';
  if (length <= 10) return '4_10';
  if (length <= 25) return '11_25';
  return '26_plus';
};

export const getAnalyticsResultCountBucket = count => {
  const normalizedCount = Number(count);

  if (!Number.isFinite(normalizedCount) || normalizedCount < 0) return 'unknown';
  if (normalizedCount === 0) return '0';
  if (normalizedCount <= 5) return '1_5';
  if (normalizedCount <= 25) return '6_25';
  if (normalizedCount <= 100) return '26_100';
  return '101_plus';
};

export const buildFeatureUsageMetadata = metadata => {
  const raw = metadata || {};
  const result = {};

  const addNormalized = (key, value) => {
    if (value) result[key] = normalizeAnalyticsValue(value);
  };

  addNormalized('event_name', raw.event_name);
  addNormalized('feature_area', raw.feature_area);
  addNormalized('feature_name', raw.feature_name);
  addNormalized('feature_action', raw.feature_action);
  addNormalized('screen_id', raw.screen_id);
  addNormalized('source_surface', raw.source_surface);
  addNormalized('workflow_id', raw.workflow_id);
  addNormalized('workflow_step', raw.workflow_step);
  addNormalized('object_type', raw.object_type);
  addNormalized('outcome', raw.outcome);
  addNormalized('error_code', raw.error_code);
  addNormalized('entry_point', raw.entry_point);
  addNormalized('user_role', raw.user_role);
  addNormalized('tenant_type', raw.tenant_type);
  addNormalized('search_scope', raw.search_scope);
  addNormalized('query_length_bucket', raw.query_length_bucket);
  addNormalized('report_type', raw.report_type);

  if (raw.release_id) result.release_id = normalizeWhitespace(raw.release_id);
  if (raw.customer_id) result.customer_id = raw.customer_id;
  if (raw.has_query !== undefined) result.has_query = Boolean(raw.has_query);

  return result;
};
